import { zodResolver } from "@hookform/resolvers/zod";
import AppLayout from "layouts/appLayout/AppLayout";
import {
  conainsUpperCase,
  containsLowerCase,
  containsNumber,
  containsSpecialCharacters,
} from "pages/auth/signup/signupCard/SignupCard";
import { useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { UserController } from "services/user/UserController";
import { Button, Card, Input } from "stories";
import { PasswordChecker } from "stories/components/passwordSlider";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import classes from "../Profile.module.scss";

const getDefaultValues = () => ({
  oldPassword: "",
  password: "",
  confirmPassword: "",
});

const schema = z
  .object({
    oldPassword: z.string().min(1, "This field is required"),
    newPassword: z
      .string()
      .min(8, "Please enter at least 8 characters")
      .refine(
        (str) => conainsUpperCase(str),
        "This field should contain at least 1 uppercase letter"
      )
      .refine(
        (str) => containsLowerCase(str),
        "This field should contain at least 1 lower letter"
      )
      .refine(
        (str) => containsNumber(str),
        "This field should contain at least 1 number"
      )
      .refine(
        (str) => containsSpecialCharacters(str),
        "One special character (!@#$%...) should be used in this field."
      ),
    confirmPassword: z.string().min(1, "This field is required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const ChangePassword = () => {
  const {
    formState: { errors },
    ...form
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(schema),
  });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = UserController();
  const [fetchState, sendRequest] = useFetchState();
  const handleChangePassword = (body) => {
    sendRequest(user.updatePassword, body);
  };
  const password = useWatch({
    control: form.control,
    name: "newPassword",
  });
  useEffect(() => {
    if (
      !fetchState.isLoading &&
      !fetchState.isError &&
      fetchState.data === ""
    ) {
      showToast("Password Changed!", "success");
      navigate("/profile");
    }
  }, [fetchState]); //eslint-disable-line
  return (
    <AppLayout title={"Change Password"} hideSidebar backButton>
      <Helmet>
        <title>Change Password | Fundever</title>
      </Helmet>
      <Container fluid>
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <div className={classes["card-wrapper"]}>
              <Card>
                <div className={classes["content-wrapper"]}>
                  <div className={"card-title"}>Change Password</div>
                  <form onSubmit={form.handleSubmit(handleChangePassword)}>
                    <Row>
                      <Col xs={12}>
                        <Input
                          type={"password"}
                          label={"Current Password"}
                          placeholder={"Input Password Here"}
                          {...form.register("oldPassword")}
                          error={errors?.oldPassword?.message}
                        />
                      </Col>
                      <Col xs={12}>
                        <Input
                          type={"password"}
                          label={"New Password"}
                          placeholder={"Input Password Here"}
                          {...form.register("newPassword")}
                          error={errors?.newPassword?.message}
                        />
                        <PasswordChecker password={password} />
                      </Col>
                      <Col xs={12}>
                        <Input
                          type={"password"}
                          label={"Repeat New Password"}
                          placeholder={"Input Password Here"}
                          {...form.register("confirmPassword")}
                          error={errors?.confirmPassword?.message}
                        />
                      </Col>
                    </Row>
                    <Row className={"mt-5"}>
                      <Col xs={12}>
                        <Button label={"Save"} loading={fetchState.isLoading} />
                      </Col>
                    </Row>
                  </form>
                  <Row className={"mt-3"}>
                    <Col xs={12}>
                      <Button
                        label={"Cancel"}
                        variant={"gray"}
                        onClick={() => navigate("/profile")}
                      />
                    </Col>
                  </Row>
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </AppLayout>
  );
};

export default ChangePassword;
